import React, { useEffect, useMemo, useState } from "react";

/* Sort icon */
const SortIcon = ({ field, sortConfig }) => {
  const isActive = sortConfig.key === field;
  const isAsc = isActive && sortConfig.direction === "asc";

  return (
    <svg
      className={`w-3.5 h-3.5 transition-colors flex-shrink-0 ${
        isActive
          ? "text-indigo-500"
          : "text-gray-300 group-hover:text-gray-400"
      }`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      {!isActive || isAsc ? (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d="M7 11l5-5m0 0l5 5m-5-5v12"
        />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d="M17 13l-5 5m0 0l-5-5m5 5V6"
        />
      )}
    </svg>
  );
};

/* Avatar initials */
const Avatar = ({ name }) => (
  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold select-none">
    {name?.charAt(0) || "?"}
  </div>
);

const ParticipantsModal = ({ isOpen, onClose, course, rows = [] }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onEsc);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onEsc);
    };
  }, [isOpen, onClose]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return rows.filter((row) => {
      return (
        row.name?.toLowerCase().includes(term) ||
        row.email?.toLowerCase().includes(term) ||
        row.phone?.includes(searchTerm)
      );
    });
  }, [rows, searchTerm]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      if (!a[sortConfig.key]) return 1;
      if (!b[sortConfig.key]) return -1;

      const aVal =
        sortConfig.key === "registeredAt"
          ? new Date(a[sortConfig.key]).getTime()
          : a[sortConfig.key];

      const bVal =
        sortConfig.key === "registeredAt"
          ? new Date(b[sortConfig.key]).getTime()
          : b[sortConfig.key];

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleExport = () => {
    setIsExporting(true);

    setTimeout(() => {
      const csvContent = [
        ["שם", "אימייל", "נייד", "תאריך הרשמה"],
        ...rows.map((r) => [
          r.name,
          r.email || "",
          r.phone || "",
          r.registeredAt
            ? new Date(r.registeredAt).toLocaleString("he-IL")
            : "",
        ]),
      ]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `participants_${course?.title || "course"}.csv`;
      link.click();

      setIsExporting(false);
    }, 500);
  };

  if (!isOpen) return null;

  const thCls =
    "px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wide cursor-pointer group select-none";

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-950/60 backdrop-blur-[3px] z-[120000]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[120001] overflow-hidden" dir="rtl">
        <div className="flex h-full w-full items-start sm:items-center justify-center px-0 sm:px-4 pt-[max(12px,env(safe-area-inset-top))] sm:pt-0">
          <div
            className="
              w-full sm:max-w-3xl lg:max-w-4xl
              bg-white rounded-t-3xl sm:rounded-3xl
              shadow-2xl shadow-black/20
              flex flex-col overflow-hidden
              h-[calc(100svh-12px-env(safe-area-inset-top))]
              sm:h-[90vh]
              overscroll-contain
              will-change-transform
            "
          >
            {/* Header */}
            <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-7 py-5">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                  </svg>
                </div>

                <div className="min-w-0">
                  <h2 className="text-gray-800 font-bold text-base sm:text-lg leading-tight">
                    משתתפים בקורס
                  </h2>
                  <p className="text-gray-500 text-xs mt-0.5 truncate">
                    {course?.title ?? "קורס"} · {rows.length} נרשמים
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
                aria-label="סגור"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2 2l10 10M12 2L2 12"
                    stroke="#6b7280"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* Search + Export bar */}
            <div className="flex-shrink-0 px-5 sm:px-7 py-3.5 bg-white border-b border-gray-100 flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center">
              {/* Search */}
              <div className="relative flex-1">
                <svg
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>

                <input
                  type="text"
                  inputMode="text"
                  placeholder="חיפוש לפי שם, אימייל או טלפון..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-10 pr-9 pl-3 text-base sm:text-sm border border-gray-200 rounded-xl bg-gray-50 placeholder-gray-400 text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 focus:bg-white transition-all"
                />

                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 2l8 8M10 2L2 10"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Export */}
              <button
                onClick={handleExport}
                disabled={isExporting || rows.length === 0}
                className="flex-shrink-0 flex items-center justify-center gap-2 h-9 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm shadow-emerald-100 hover:shadow-md hover:shadow-emerald-200 hover:-translate-y-px disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none transition-all duration-150"
              >
                {isExporting ? (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                )}
                ייצוא CSV
              </button>
            </div>

            {/* Table / Empty state */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-7 py-4 scroll-pb-28">
              {sortedRows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-300 border-2 border-dashed border-gray-200 rounded-xl">
                  <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 100 8 4 4 0 000-8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
                    />
                  </svg>

                  <p className="text-sm font-medium text-gray-400">
                    {searchTerm ? "לא נמצאו תוצאות" : "אין נרשמים לקורס זה"}
                  </p>

                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="mt-2 text-xs text-indigo-500 hover:text-indigo-700 font-medium"
                    >
                      נקה חיפוש
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-gray-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className={thCls} onClick={() => handleSort("name")}>
                          <div className="flex items-center gap-1.5">
                            שם
                            <SortIcon field="name" sortConfig={sortConfig} />
                          </div>
                        </th>

                        <th
                          className={`${thCls} hidden sm:table-cell`}
                          onClick={() => handleSort("email")}
                        >
                          <div className="flex items-center gap-1.5">
                            אימייל
                            <SortIcon field="email" sortConfig={sortConfig} />
                          </div>
                        </th>

                        <th
                          className={`${thCls} hidden md:table-cell`}
                          onClick={() => handleSort("phone")}
                        >
                          <div className="flex items-center gap-1.5">
                            נייד
                            <SortIcon field="phone" sortConfig={sortConfig} />
                          </div>
                        </th>

                        <th
                          className={thCls}
                          onClick={() => handleSort("registeredAt")}
                        >
                          <div className="flex items-center gap-1.5">
                            הרשמה
                            <SortIcon
                              field="registeredAt"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-50">
                      {sortedRows.map((r, i) => (
                        <tr
                          key={r.id}
                          className={`hover:bg-indigo-50/40 transition-colors ${
                            i % 2 === 1 ? "bg-gray-50/30" : "bg-white"
                          }`}
                        >
                          <td className="px-4 py-3">                           
                            <span className="font-semibold text-gray-800 text-sm">
                              {r.name}
                            </span>                            
                          </td>

                          <td className="px-4 py-3 hidden sm:table-cell">
                            <span className="text-gray-500 text-sm">
                              {r.email || <span className="text-gray-300">—</span>}
                            </span>
                          </td>

                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className="text-gray-500 text-sm font-mono tracking-wide">
                              {r.phone || <span className="text-gray-300">—</span>}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            {r.registeredAt ? (
                              <div className="flex flex-col leading-tight">
                                <span className="text-gray-700 text-sm font-medium">
                                  {new Date(r.registeredAt).toLocaleDateString("he-IL")}
                                </span>
                                <span className="text-gray-400 text-xs">
                                  {new Date(r.registeredAt).toLocaleTimeString("he-IL", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  {sortedRows.length} מתוך {rows.length}
                </span>

                {searchTerm && (
                  <span className="flex items-center gap-1.5 text-xs text-indigo-400 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    תוצאות חיפוש
                  </span>
                )}
              </div>

              <button
                type="button"
                onClick={onClose}
                className="h-9 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ParticipantsModal;